import { computed, ref } from "vue";

type Theme = "light" | "dark" | "system";

const STORAGE_KEY = "admin-theme";

function readStored(): Theme {
  if (typeof window === "undefined") {
    return "system";
  }

  const value = window.localStorage.getItem(STORAGE_KEY);

  if (value === "light" || value === "dark" || value === "system") {
    return value;
  }

  return "system";
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

const theme = ref<Theme>(readStored());

const resolvedTheme = computed<"light" | "dark">(() => {
  if (theme.value === "system") {
    return systemPrefersDark() ? "dark" : "light";
  }

  return theme.value;
});

function applyClass(resolved: "light" | "dark"): void {
  if (typeof document === "undefined") {
    return;
  }

  const root = document.documentElement;

  if (resolved === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}

function setTheme(value: Theme): void {
  theme.value = value;

  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, value);
  }

  applyClass(resolvedTheme.value);
}

applyClass(resolvedTheme.value);

if (typeof window !== "undefined" && window.matchMedia) {
  const media = window.matchMedia("(prefers-color-scheme: dark)");

  media.addEventListener("change", () => {
    if (theme.value === "system") {
      applyClass(resolvedTheme.value);
    }
  });
}

export function useTheme() {
  return { theme, setTheme, resolvedTheme };
}
